<script lang="ts">
import { defineBlock } from '@bagelink/blox'
import { defineSchema, $, Alert } from '@bagelink/vue'
const schema = defineSchema({
	layout: $.select(
		'Layout',
		[
			{ label: 'Featured', value: 'featured' },
			{ label: 'Compact', value: 'compact' },
		],
		{ default: 'featured' },
	),
	useDatastoreData: $.checkbox('Use featured article from datastore', { default: true }),
	datastoreExplanation: $.display(Alert, {
		info: true,
		thin: true,
		title: 'The featured article is loaded automatically',
		message:
			'The tag, title, image, authors, and article link come from the datastore. Disable “Use featured article from datastore” to enter them manually.',
	}).if('useDatastoreData eq true'),
	tag: $.text('Tag').if('useDatastoreData eq false'),
	title: $.text('Title').if('useDatastoreData eq false'),
	image: $.upload('Image').if('useDatastoreData eq false'),
	link: $.text('Article Link').if('useDatastoreData eq false'),
	authors: $.array(
		'Authors',
		defineSchema({
			name: $.text('Name'),
			title: $.text('Title'),
			image: $.upload('Image'),
		}),
		{ collapsible: true },
	).if('useDatastoreData eq false'),
})
export const BlockBanner = defineBlock({
	thumbnail: '/block-thumbs/BlockBanner.svg',
	category: 'Hero',
	order: 160,
	name: 'BlockBanner',
	label: 'Banner',
	icon: 'featured_play_list',
	schema,
	defaults: {
		layout: 'featured',
		useDatastoreData: true,
		tag: 'Featured Article',
		title: '',
		image: '',
		link: '',
		authors: [],
	},
	dataSource: { collection: 'articles', store: 'the_torah', label: 'Articles' },
})
</script>
<script setup lang="ts">
import { useBloxRuntime } from '@bagelink/blox'
import { Image } from '@bagelink/vue'
import { computed } from 'vue'
import AuthorSmallLink from '@/components/blocks/AuthorSmallLink.vue'
import { useArticlesWithAuthors } from '@/composables/useDatastore'
import { articleTitleText, sanitizeArticleTitleHtml } from '@/utils/articleHtml'
import { displayImage } from '@/utils/image'



const props = defineProps<{
	layout?: 'featured' | 'compact'
	useDatastoreData?: boolean
	tag?: string
	title?: string
	image?: string
	link?: string
	authors?: { name: string; title: string; image: string; slug?: string; link?: string }[]
}>()

const PREVIEW_ARTICLE = {
	tag: 'Featured Article',
	title: 'Understanding the Torah in Its Ancient Context',
	image: '/og.jpg',
	link: '/',
	authors: [{ name: 'Sample Author', title: 'Biblical Scholar', image: '/webclip.png' }],
}

const { isPreview } = useBloxRuntime()
const { articles, loading } = useArticlesWithAuthors({ limit: 30 })
const featured = computed(
	() => articles.value.find((article: any) => article.featuredHomepage) ?? articles.value[0],
)
const content = computed(() => {
	if (props.useDatastoreData === false) {
		return {
			tag: props.tag || '',
			title: props.title || '',
			image: props.image || '',
			link: props.link || '/',
			authors: props.authors || [],
		}
	}
	const article = featured.value as any | undefined
	if (!article && !loading.value && isPreview.value) return PREVIEW_ARTICLE
	return {
		tag: article?.titleTag || '',
		title: article?.title || '',
		image: article?.image || '',
		link: article?.link || (article?.slug ? `/article/${article.slug}` : '/'),
		authors: article?.authors || [],
	}
})
</script>

<template>
	<div v-if="props.layout === 'compact'" class="bg-gray p-075 w850px mt-1 flex gap-1 m_w-100p">
		<RouterLink :to="content.link" class="decoration-none m_none">
			<Image :src="displayImage(content.image, 'card')" :alt="articleTitleText(content.title)" ratio="2-1" class="ratio-2-1 h-120px" lazy />
		</RouterLink>
		<div class="txt-center w-100p">
			<p class="txt14 color-gray-70 pb-05 m_txt14">
				{{ content.tag }}
			</p>
			<RouterLink :to="content.link" class="decoration-none hover-underline">
				<h3 class="txt24 m-0 regular m_txt20 line-height-13 balance" v-html="sanitizeArticleTitleHtml(content.title)" />
			</RouterLink>
		</div>
	</div>
	<div v-else class="m_px-05">
		<div class="bg-gray p-1 w1100px grid gap-2 banner-grid m_p-0 m_block m_txt-center my-2">
			<RouterLink :to="content.link" class="decoration-none block m_mb-1">
				<Image
					:src="displayImage(content.image, 'card')"
					:alt="articleTitleText(content.title)"
					class="w100p h100px cover m_h120px block"
					lazy
				/>
			</RouterLink>
			<div>
				<div class="pb-05 m_px-1">
					<p class="color-primary mb-025 txt17 line-height-12">
						{{ content.tag }}
					</p>
					<RouterLink :to="content.link" class="decoration-none hover-underline">
						<h3 class="my-0 txt22 regular line-height-13 balance" v-html="sanitizeArticleTitleHtml(content.title)" />
					</RouterLink>
				</div>

				<!-- Authors -->
				<div class="-mb-05 m_m-0 m_px-1 m_pb-05">
					<AuthorSmallLink
						v-for="author in content.authors"
						:key="author.name"
						:author="author"
					/>
				</div>
			</div>
		</div>
	</div>
</template>

<style scoped>
.banner-grid {
	grid-template-columns: 160px auto;
}
</style>
